import { useEffect, useMemo, useState } from 'react'
import AdminShell from './AdminShell'
import { tournamentApi } from '../../services/api'

/**
 * Super-admin: CTQ split-payment oversight. Lists every SENT split quote with its
 * current status + hold, and lets an admin reset a lapsed split to a fresh 48h hold
 * so the golfers' existing invite links work again (no need to make the customer
 * redo the split). Reset only extends the hold + flips 'expired' -> 'collecting';
 * it never charges or refunds.
 */
const money = (n, cur = 'CAD') =>
  n == null ? '—' : new Intl.NumberFormat('en-CA', { style: 'currency', currency: cur }).format(Number(n))
function fmtDateTime(v) {
  if (!v) return '—'
  const d = new Date(v)
  return isNaN(d) ? '—' : d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
function fmtDay(v) {
  if (!v) return '—'
  const ymd = String(v).slice(0, 10)
  const d = new Date(ymd + 'T00:00:00Z')
  return isNaN(d) ? ymd : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
}

const SPLIT_BADGE = {
  collecting: ['#dbeafe', '#1e40af', 'Collecting'],
  booking: ['#fef3c7', '#92400e', 'Booking'],
  booked: ['#dcfce7', '#166534', 'Booked'],
  expired: ['#fff7ed', '#9a3412', 'Expired'],
  cancelled: ['#fee2e2', '#991b1b', 'Cancelled'],
}

export default function SplitQuotes() {
  const [quotes, setQuotes] = useState(null)
  const [err, setErr] = useState(null)
  const [q, setQ] = useState('')
  const [busyId, setBusyId] = useState(null)
  const [busyAction, setBusyAction] = useState(null)

  function load() {
    setErr(null)
    tournamentApi.get('/admin/split-quotes')
      .then(r => setQuotes(r.data?.quotes || []))
      .catch(e => setErr(e?.response?.data?.message || e.message))
  }
  useEffect(() => { load() }, [])

  const rows = useMemo(() => {
    if (!quotes) return []
    const needle = q.trim().toLowerCase()
    if (!needle) return quotes
    return quotes.filter(x => [x.quoteNumber, x.resellerName, x.organizerName, x.organizerEmail, x.name]
      .filter(Boolean).join(' ').toLowerCase().includes(needle))
  }, [quotes, q])

  async function resetHold(row) {
    if (!window.confirm(`Reset the price hold for ${row.quoteNumber} to 48 hours from now?\n\nThis reopens the split for payment (its existing invite links go live again). It does not charge anyone.`)) return
    setBusyId(row.id); setBusyAction('reset')
    try {
      const { data } = await tournamentApi.post(`/admin/split-quotes/${row.id}/reset-hold`)
      setQuotes(qs => qs.map(x => x.id === row.id
        ? { ...x, splitStatus: data.splitStatus, holdExpiresAt: data.holdExpiresAt, holdExpired: false, extendCount: 0, canReset: true }
        : x))
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setBusyId(null); setBusyAction(null) }
  }

  // Recover a split whose non-lead golfers lost their share + pay-link (e.g. a
  // roster re-save mid-collection wiped them). Re-splits the REMAINING owed
  // evenly among the wiped golfers, re-issues their links + re-emails invites;
  // the lead and any still-assigned golfer are untouched. Never charges.
  async function repairRoster(row) {
    const n = row.missingShares || 0
    if (!window.confirm(`Repair the roster for ${row.quoteNumber}?\n\nThis re-splits the remaining amount owed evenly among the ${n} golfer${n === 1 ? '' : 's'} who lost their share, re-issues their “Pay my share” links, and emails them an invite. The organizer and any golfer who already has a share are left unchanged. It does not charge anyone.`)) return
    setBusyId(row.id); setBusyAction('repair')
    try {
      const { data } = await tournamentApi.post(`/admin/split-quotes/${row.id}/repair-roster`)
      const rep = data.repaired || []
      const emailed = rep.filter(r => r.emailed).map(r => r.name || `#${r.id}`)
      const notEmailed = rep.filter(r => !r.emailed).map(r => r.name || `#${r.id}`)
      let msg = `Repaired ${rep.length} golfer${rep.length === 1 ? '' : 's'}.`
      if (data.remainingOwed > 0) msg += `\nRe-split ${money(data.remainingOwed, row.currency)} among the golfers who lost their share.`
      if (emailed.length) msg += `\n\nInvite emailed to: ${emailed.join(', ')}.`
      // Name who did NOT get an email (no address, or the send failed) so staff can
      // copy that golfer's pay link from the quote — the link itself stays off this page.
      if (notEmailed.length) msg += `\n\nNOT emailed (no address or send failed) — copy their “Pay my share” link from the quote for: ${notEmailed.join(', ')}.`
      alert(msg)
      load()
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setBusyId(null); setBusyAction(null) }
  }

  const expiredCount = (quotes || []).filter(x => x.holdExpired && x.canReset).length

  return (
    <AdminShell title="Split Payments">
      <div style={{ fontSize: 13.5, color: '#475569', lineHeight: 1.5, marginBottom: 14, maxWidth: 760 }}>
        Every custom-trip quote that was sent with split payments. If a split's price hold lapsed
        (e.g. under the old 30-minute window), click <strong>Reset to 48h</strong> to reopen it — the
        golfers' existing “Pay my share” links go live again, no need to have the customer redo anything.
        Resetting only extends the hold; it never charges or refunds. Note: any golfer who had already
        authorized before it lapsed had that hold released, so they'll re-enter their card (the Paid
        column shows who's currently in). If a split lost some golfers' pay links (a roster re-save
        mid-collection can wipe them), <strong>Repair roster</strong> re-splits the remaining balance
        among those golfers and re-sends their “Pay my share” links — the organizer and already-paid
        golfers are untouched, and nobody is charged.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search quote #, reseller, organizer…"
          style={{ flex: '1 1 320px', maxWidth: 420, padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13.5 }} />
        {quotes && (
          <div style={{ fontSize: 12.5, color: '#64748b' }}>
            {quotes.length} split quote{quotes.length === 1 ? '' : 's'}
            {expiredCount > 0 && <span style={{ color: '#9a3412', fontWeight: 700 }}> · {expiredCount} expired, resettable</span>}
          </div>
        )}
        <button type="button" onClick={load} style={{ border: '1px solid #e2e8f0', background: '#fff', color: '#475569', borderRadius: 8, fontSize: 13, fontWeight: 600, padding: '7px 12px', cursor: 'pointer' }}>Refresh</button>
      </div>

      {err && <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13 }}>{err}</div>}

      {quotes == null ? (
        <div style={{ padding: 14, color: '#94a3b8', fontSize: 13 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', border: '1px dashed #cbd5e1', borderRadius: 10, color: '#94a3b8', fontSize: 13.5 }}>
          {quotes.length === 0 ? 'No sent split-payment quotes yet.' : 'No quotes match your search.'}
        </div>
      ) : (
        <div style={{ border: '1px solid #eef2f7', borderRadius: 10, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: '#f8fafc', textAlign: 'left' }}>
              {['Quote', 'Reseller', 'Organizer', 'Trip', 'Trip date', 'Split', 'Hold expires', 'Paid', ''].map((h, i) => (
                <th key={i} style={{ padding: '9px 12px', fontSize: 11.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {rows.map(row => {
                const [bg, fg, label] = SPLIT_BADGE[row.splitStatus] || ['#f1f5f9', '#475569', row.splitStatus || '—']
                return (
                  <tr key={row.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#0f172a', whiteSpace: 'nowrap' }}>{row.quoteNumber}</td>
                    <td style={{ padding: '9px 12px', color: '#475569' }}>{row.resellerName || '—'}</td>
                    <td style={{ padding: '9px 12px', color: '#475569' }}>
                      <div style={{ fontWeight: 600, color: '#0f172a' }}>{row.organizerName || '—'}</div>
                      {row.organizerEmail && <div style={{ fontSize: 11.5, color: '#94a3b8' }}>{row.organizerEmail}</div>}
                    </td>
                    <td style={{ padding: '9px 12px', color: '#475569', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name || <span style={{ color: '#cbd5e1' }}>Untitled</span>}</td>
                    <td style={{ padding: '9px 12px', color: '#475569', whiteSpace: 'nowrap' }}>{fmtDay(row.tripStartDate)}</td>
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: bg, color: fg }}>{label}</span>
                    </td>
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', color: row.holdExpired ? '#b91c1c' : '#475569' }}>
                      {fmtDateTime(row.holdExpiresAt)}{row.holdExpired && <span style={{ fontWeight: 700 }}> · expired</span>}
                    </td>
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', fontWeight: 700, color: '#0f172a' }}>
                      {row.authorizedCount}/{row.totalGolfers}
                      {row.missingShares > 0 && (
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#b45309' }}>{row.missingShares} missing link{row.missingShares === 1 ? '' : 's'}</div>
                      )}
                    </td>
                    <td style={{ padding: '9px 12px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                      {(row.canRepair || row.canReset) ? (
                        <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                          {row.canRepair && (
                            <button type="button" onClick={() => repairRoster(row)} disabled={busyId === row.id}
                              title={`${row.missingShares} golfer(s) lost their share + pay link — re-split the remainder and re-send their links`}
                              style={{ border: '1px solid #b45309', background: '#fff', color: '#b45309', borderRadius: 6, fontSize: 12, fontWeight: 700, padding: '5px 10px', cursor: busyId === row.id ? 'default' : 'pointer', opacity: busyId === row.id ? 0.6 : 1 }}>
                              {busyId === row.id && busyAction === 'repair' ? 'Repairing…' : 'Repair roster'}
                            </button>
                          )}
                          {row.canReset && (
                            <button type="button" onClick={() => resetHold(row)} disabled={busyId === row.id}
                              style={{ border: '1px solid #0f766e', background: '#fff', color: '#0f766e', borderRadius: 6, fontSize: 12, fontWeight: 700, padding: '5px 10px', cursor: busyId === row.id ? 'default' : 'pointer', opacity: busyId === row.id ? 0.6 : 1 }}>
                              {busyId === row.id && busyAction === 'reset' ? 'Resetting…' : 'Reset to 48h'}
                            </button>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: 11.5, color: '#94a3b8' }}>—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  )
}
